/*
  ayume© · grand logo du footer, déformation au toucher (WebGL).
  Référence validée par Romain le 29/09/2026 (PC + iPhone) :
  https://claude.ai/artifact/CBvkbtCV7iWENKjoqWwWmw

  Valeurs retenues : intensité 1.0 ; rayon 0.8 × hauteur du logo ; aberration 1.0.
  Garde-fous :
  - le SVG statique du snippet ayume-logo reste en place et visible tant que le WebGL n'a pas dessiné ;
    sans WebGL, ou avec prefers-reduced-motion, rien ne change ;
  - initialisation seulement quand le footer approche de l'écran, pause dès qu'il sort ;
  - écouteurs tactiles passifs, aucun preventDefault : le défilement vertical n'est jamais bloqué.
*/
const CFG = { int: 1.0, rad: 0.8, ab: 1.0 };

function start(wordmark) {
  const footer = wordmark.closest('.av-footer');
  const brand = wordmark.querySelector('.av-brand');
  const svg = brand && brand.querySelector('svg');
  const path = svg && svg.querySelector('path');
  if (!footer || !svg || !path) return null;       // image personnalisée dans les réglages : pas d'effet
  const vb = svg.viewBox && svg.viewBox.baseVal;
  const VB_W = vb && vb.width ? vb.width : 2400;
  const pathD = path.getAttribute('d');
  const cfg = CFG;

  const canvas = document.createElement('canvas');
  canvas.className = 'av-logo-gl';
  canvas.setAttribute('aria-hidden', 'true');
  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'high-performance' });
  if (!gl) return null;
  footer.appendChild(canvas);

  const VS = `attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}`;
  const FS = `
precision highp float;
uniform sampler2D uLogo, uHeight, uField;
uniform vec2 uRes;       // canvas, px écran
uniform vec2 uPtr;       // position de la lentille, px écran (origine en haut à gauche)
uniform vec2 uGhost;     // respiration
uniform float uHover, uRadius, uLens, uAb, uAbCap, uM, uDpr, uSpeed, uTime, uGhostR, uGhostL;
uniform vec3 uPaper;

float logoA(vec2 px){ return texture2D(uLogo, px / uRes).a; }
float hgt(vec2 px){ return texture2D(uHeight, px / uRes).r; }

void main(){
  vec2 px = vec2(gl_FragCoord.x, uRes.y - gl_FragCoord.y);
  vec2 uv = px / uRes;

  // Champ élastique simulé (encodage racine carrée, zéro exact à 128)
  vec4 f = texture2D(uField, uv);
  vec2 s2 = (f.rg * 255.0 - 128.0) / 127.0;
  vec2 d = sign(s2) * s2 * s2 * uM;
  float energy = f.b;

  // Lentille de verre sous le pointeur
  vec2 q = px - uPtr;
  float r = length(q);
  float k = clamp(1.0 - (r * r) / (uRadius * uRadius), 0.0, 1.0);
  k *= k;
  vec2 lensD = q * uLens * k * uHover;

  // Respiration : lentille fantôme très faible
  vec2 qg = px - uGhost;
  float kg = clamp(1.0 - dot(qg, qg) / (uGhostR * uGhostR), 0.0, 1.0);
  kg *= kg;
  vec2 ghostD = qg * uGhostL * kg;

  vec2 act = d + lensD;                 // ce qui bouge vraiment (la respiration reste hors relief et hors couleur)
  vec2 tot = act + ghostD;
  vec2 s = px - tot;
  float mag = length(act);
  vec2 dir = mag > 0.001 ? act / mag : vec2(0.0);

  // Dispersion : 7 échantillons répartis sur le spectre, plus forte là où la matière bouge et sur l'anneau de la lentille
  float ring = 4.0 * k * (1.0 - k) * uHover * (0.6 + uSpeed);
  float abPx = uAb * uDpr * min(uAbCap, 0.16 * mag / uDpr + 7.0 * ring + 6.0 * energy);
  vec2 o = dir * abPx;
  vec3 acc = vec3(0.0), wsum = vec3(0.0);
  float jit = (fract(sin(dot(px, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 3.0;   // tramage : pas de copies en escalier
  for (int i = 0; i < 7; i++) {
    float tt = float(i) / 3.0 - 1.0 + jit;
    vec3 w = max(vec3(0.0), 1.0 - abs(vec3(tt) - vec3(0.67, 0.0, -0.67)) / 0.9);
    float a = logoA(s + o * tt);
    acc += a * w; wsum += w;
  }
  vec3 rgbA = acc / wsum;
  float aR = rgbA.r, aG = rgbA.g, aB = rgbA.b;

  // Lumière qui se courbe : relief tiré de la carte de hauteur, seulement là où ça bouge
  float bump = clamp(clamp(mag / (10.0 * uDpr), 0.0, 1.0) * 0.9 + k * uHover * 0.85 + energy * 0.6, 0.0, 1.0);
  float e = 2.5 * uDpr;
  vec2 g = vec2(hgt(s + vec2(e, 0.0)) - hgt(s - vec2(e, 0.0)), hgt(s + vec2(0.0, e)) - hgt(s - vec2(0.0, e)));
  vec2 n = -g * 3.2 * bump;                          // pente de la surface
  vec2 toL = uPtr - px;
  vec2 ld = toL / (length(toL) + 1.0);
  float facing = dot(n, ld);
  float slope = length(n);
  float shade = clamp(1.0 + facing * 0.9 - slope * 0.25, 0.45, 1.12);
  float spec = pow(clamp(facing * 1.4, 0.0, 1.0), 2.0) * 0.55;
  vec3 film = 0.5 + 0.5 * cos(6.2831 * (slope * 2.2 + dot(dir, vec2(0.35, 0.2)) + uTime * 0.04 + vec3(0.0, 0.33, 0.67)));

  vec3 col = uPaper * vec3(aR, aG, aB) * shade;
  col += vec3(spec) * aG;
  col += film * min(slope, 1.0) * 0.32 * aG;

  // Halo : la lumière déborde un peu autour des lettres près du pointeur
  float hb = hgt(s);
  float halo = hb * (1.0 - aG) * (k * uHover * 0.2 + energy * 0.08);
  col += uPaper * halo;

  float a = clamp(max(aR, max(aG, aB)) + halo, 0.0, 1.0);
  gl_FragColor = vec4(min(col, vec3(a)), a);
}`;

  const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; };
  let prog, U = {}, texLogo, texHeight, texField;
  const initGL = () => {
    prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    ['uLogo', 'uHeight', 'uField', 'uRes', 'uPtr', 'uGhost', 'uHover', 'uRadius', 'uLens', 'uAb', 'uAbCap', 'uM', 'uDpr', 'uSpeed', 'uTime', 'uGhostR', 'uGhostL', 'uPaper']
      .forEach((n) => { U[n] = gl.getUniformLocation(prog, n); });
    const mk = (unit) => { const t = gl.createTexture(); gl.activeTexture(gl.TEXTURE0 + unit); gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); return t; };
    texLogo = mk(0); texHeight = mk(1); texField = mk(2);
    gl.uniform1i(U.uLogo, 0); gl.uniform1i(U.uHeight, 1); gl.uniform1i(U.uField, 2);
    gl.uniform3f(U.uPaper, 0xF1 / 255, 0xEF / 255, 0xEA / 255);
    gl.clearColor(0, 0, 0, 0);
  };
  try { initGL(); } catch (err) { canvas.remove(); return null; }

  /* ---------- Géométrie ---------- */
  const G = { cssW: 0, cssH: 0, dpr: 1, logoX: 0, logoY: 0, logoW: 0, logoH: 0, GX: 0, GY: 0, M: 1 };
  let dX, dY, vX, vY, tX, tY, fieldBytes;

  const boxBlur = (src, w, h, r) => {
    const tmp = new Float32Array(w * h), out = src;
    for (let pass = 0; pass < 3; pass++) {
      for (let y = 0; y < h; y++) { let acc = 0; const row = y * w;
        for (let x = -r; x <= r; x++) acc += out[row + Math.min(w - 1, Math.max(0, x))];
        for (let x = 0; x < w; x++) { tmp[row + x] = acc / (2 * r + 1);
          acc += out[row + Math.min(w - 1, x + r + 1)] - out[row + Math.max(0, x - r)]; } }
      for (let x = 0; x < w; x++) { let acc = 0;
        for (let y = -r; y <= r; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
        for (let y = 0; y < h; y++) { out[y * w + x] = acc / (2 * r + 1);
          acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x]; } }
    }
    return out;
  };


  const layout = () => {
    const fr = footer.getBoundingClientRect(), lr = svg.getBoundingClientRect();
    if (!fr.width || !lr.width) return false;
    const logoH = lr.height;
    const padTop = Math.round(logoH * 0.45);
    const padBot = Math.max(0, Math.round(Math.min(logoH * 0.45, fr.bottom - lr.bottom)));
    G.cssW = Math.round(fr.width);
    G.cssH = Math.round(logoH + padTop + padBot);
    G.logoX = lr.left - fr.left; G.logoY = padTop; G.logoW = lr.width; G.logoH = logoH;
    G.dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(3.2e6 / (G.cssW * G.cssH)));
    canvas.style.width = G.cssW + 'px'; canvas.style.height = G.cssH + 'px';
    canvas.style.top = (lr.top - fr.top - padTop) + 'px';
    const W = Math.max(1, Math.round(G.cssW * G.dpr)), H = Math.max(1, Math.round(G.cssH * G.dpr));
    canvas.width = W; canvas.height = H;
    gl.viewport(0, 0, W, H);

    // Texture du logo : le tracé vectoriel exact du SVG, à la résolution du canvas
    const c = document.createElement('canvas'); c.width = W; c.height = H;
    const x = c.getContext('2d');
    const sc = (G.logoW * G.dpr) / VB_W;
    x.setTransform(sc, 0, 0, sc, G.logoX * G.dpr, G.logoY * G.dpr);
    x.fillStyle = '#fff'; x.fill(new Path2D(pathD));
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texLogo);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);

    // Carte de hauteur : logo flouté à 1/4, pour le relief et le halo
    const hw = Math.max(8, Math.round(W / 4)), hh = Math.max(8, Math.round(H / 4));
    const hc = document.createElement('canvas'); hc.width = hw; hc.height = hh;
    const hx = hc.getContext('2d');
    const kx = hw / W, ky = hh / H;
    hx.setTransform(sc * kx, 0, 0, sc * ky, G.logoX * G.dpr * kx, G.logoY * G.dpr * ky);
    hx.fillStyle = '#fff'; hx.fill(new Path2D(pathD));
    const id = hx.getImageData(0, 0, hw, hh).data;
    const a = new Float32Array(hw * hh);
    for (let i = 0; i < a.length; i++) a[i] = id[i * 4 + 3] / 255;
    boxBlur(a, hw, hh, Math.max(2, Math.round(G.logoH * G.dpr / 4 * 0.03)));
    const hb = new Uint8Array(hw * hh);
    for (let i = 0; i < a.length; i++) hb[i] = Math.min(255, Math.round(a[i] * 255));
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, texHeight);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, hw, hh, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, hb);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);

    // Grille de simulation
    G.GX = G.cssW > 900 ? 128 : 96;
    G.GY = Math.max(12, Math.round(G.GX * G.cssH / G.cssW));
    const N = G.GX * G.GY;
    dX = new Float32Array(N); dY = new Float32Array(N); vX = new Float32Array(N); vY = new Float32Array(N);
    tX = new Float32Array(N); tY = new Float32Array(N);
    fieldBytes = new Uint8Array(N * 4);
    G.M = G.logoH * 0.45;
    for (let i = 0; i < N; i++) { fieldBytes[i * 4] = 128; fieldBytes[i * 4 + 1] = 128; fieldBytes[i * 4 + 2] = 0; fieldBytes[i * 4 + 3] = 255; }
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, texField);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, G.GX, G.GY, 0, gl.RGBA, gl.UNSIGNED_BYTE, fieldBytes);
    return true;
  };

  /* ---------- Entrées : souris, doigt, défilement ---------- */
  const P = { cx: 0, cy: 0, has: false, touch: false, touchDown: false, x: 0, y: 0, px: 0, py: 0, vx: 0, vy: 0, active: false, lx: 0, ly: 0 };
  let pokes = [];
  let rect = canvas.getBoundingClientRect();
  const inside = (cx, cy, m = 0) => cx >= rect.left - m && cx <= rect.right + m && cy >= rect.top - m && cy <= rect.bottom + m;
  const poke = (cx, cy) => { rect = canvas.getBoundingClientRect(); if (inside(cx, cy)) pokes.push({ x: cx - rect.left, y: cy - rect.top }); wake(); };

  const ac = new AbortController();
  const opt = { passive: true, signal: ac.signal };
  window.addEventListener('pointermove', (e) => { if (e.pointerType === 'touch') return; P.cx = e.clientX; P.cy = e.clientY; P.has = true; P.touch = false; wake(); }, opt);
  window.addEventListener('pointerdown', (e) => { if (e.pointerType === 'touch') return; poke(e.clientX, e.clientY); }, opt);
  document.documentElement.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') P.has = false; }, opt);
  window.addEventListener('blur', () => { P.has = false; P.touchDown = false; }, opt);
  // Écouteurs passifs : jamais de preventDefault, le défilement reste natif.
  window.addEventListener('touchstart', (e) => { const t = e.touches[0]; P.cx = t.clientX; P.cy = t.clientY; P.has = true; P.touch = true; P.touchDown = true; poke(t.clientX, t.clientY); }, opt);
  window.addEventListener('touchmove', (e) => { const t = e.touches[0]; if (!t) return; P.cx = t.clientX; P.cy = t.clientY; wake(); }, opt);
  const endTouch = (e) => { if (e.touches.length === 0) { P.touchDown = false; P.has = false; } };
  window.addEventListener('touchend', endTouch, opt);
  window.addEventListener('touchcancel', endTouch, opt);
  let lastScrollY = window.scrollY, scrollV = 0;
  window.addEventListener('scroll', () => wake(), opt);

  /* ---------- Simulation élastique ---------- */
  const K = 170, C = 6.5, S = 900, KC = 22, DT = 1 / 120;
  const step = (hoverActive, R) => {
    const { GX, GY } = G, cw = G.cssW / GX, ch = G.cssH / GY;
    const R2 = R * R, drag = 0.3 * cfg.int;
    const pvx = P.vx * drag, pvy = P.vy * drag;
    // bornes de la zone influencée par le pointeur
    let i0 = 0, i1 = -1, j0 = 0, j1 = -1;
    if (hoverActive) { i0 = Math.max(0, Math.floor((P.x - 2.2 * R) / cw)); i1 = Math.min(GX - 1, Math.ceil((P.x + 2.2 * R) / cw)); j0 = Math.max(0, Math.floor((P.y - 2.2 * R) / ch)); j1 = Math.min(GY - 1, Math.ceil((P.y + 2.2 * R) / ch)); }
    for (let j = 0; j < GY; j++) {
      const ju = j > 0 ? j - 1 : j, jd = j < GY - 1 ? j + 1 : j;
      for (let i = 0; i < GX; i++) {
        const n = j * GX + i, il = i > 0 ? n - 1 : n, ir = i < GX - 1 ? n + 1 : n, nu = ju * GX + i, nd = jd * GX + i;
        let ax = -K * (dX[n] - tX[n]) - C * vX[n] + S * (dX[il] + dX[ir] + dX[nu] + dX[nd] - 4 * dX[n]) * 0.25;
        let ay = -K * (dY[n] - tY[n]) - C * vY[n] + S * (dY[il] + dY[ir] + dY[nu] + dY[nd] - 4 * dY[n]) * 0.25;
        if (i >= i0 && i <= i1 && j >= j0 && j <= j1) {
          const cx = (i + 0.5) * cw - P.x, cy = (j + 0.5) * ch - P.y;
          const w = Math.exp(-(cx * cx + cy * cy) / R2);
          ax += w * KC * (pvx - vX[n]); ay += w * KC * (pvy - vY[n]);
        }
        vX[n] += ax * DT; vY[n] += ay * DT;
      }
    }
    const M = G.M;
    for (let n = 0; n < dX.length; n++) {
      dX[n] += vX[n] * DT; dY[n] += vY[n] * DT;
      const m2 = dX[n] * dX[n] + dY[n] * dY[n];
      if (m2 > M * M) { const f = M / Math.sqrt(m2); dX[n] *= f; dY[n] *= f; }
    }
  };
  const applyPokes = (R) => {
    if (!pokes.length) return;
    const { GX, GY } = G, cw = G.cssW / GX, ch = G.cssH / GY, R2 = R * R * 0.6;
    for (const p of pokes) for (let j = 0; j < GY; j++) for (let i = 0; i < GX; i++) {
      const n = j * GX + i, cx = (i + 0.5) * cw - p.x, cy = (j + 0.5) * ch - p.y, r = Math.hypot(cx, cy) + 1e-3;
      const w = Math.exp(-(r * r) / R2) * 520 * cfg.int;
      vX[n] += cx / r * w; vY[n] += cy / r * w;
    }
    pokes = [];
  };
  const upload = () => {
    const M = G.M, b = fieldBytes; let energy = 0;
    const enc = (v) => { const s = Math.sign(v) * Math.sqrt(Math.min(1, Math.abs(v) / M)); return Math.round(128 + 127 * s); };
    for (let n = 0; n < dX.length; n++) {
      const sp = Math.hypot(vX[n], vY[n]);
      energy += sp + Math.abs(dX[n]) + Math.abs(dY[n]);
      b[n * 4] = enc(dX[n]); b[n * 4 + 1] = enc(dY[n]); b[n * 4 + 2] = Math.min(255, sp * 0.25);
    }
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, texField);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, G.GX, G.GY, gl.RGBA, gl.UNSIGNED_BYTE, b);
    return energy / dX.length;
  };


  /* ---------- Boucle ---------- */
  let running = false, visible = false, raf = 0, last = 0, hover = 0, speed = 0, idleFlip = false, energy = 0, firstFrame = true;
  const t0 = performance.now();
  const frame = (now) => {
    raf = 0;
    if (!visible || document.hidden) { running = false; return; }
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, Math.max(0.001, (now - (last || now - 16.7)) / 1000));
    last = now;

    rect = canvas.getBoundingClientRect();
    const sy = window.scrollY; const rawSV = (sy - lastScrollY) / dt; lastScrollY = sy;
    scrollV += (Math.max(-4000, Math.min(4000, rawSV)) - scrollV) * 0.3;

    // Pointeur dans le repère du canvas (le défilement le fait bouger aussi)
    const R = cfg.rad * G.logoH;
    const on = P.has && (!P.touch || P.touchDown) && inside(P.cx, P.cy, R * 0.25);
    const x = P.cx - rect.left, y = P.cy - rect.top;
    if (on && !P.active) { P.x = P.px = x; P.y = P.py = y; P.lx = x; P.ly = y; P.vx = P.vy = 0; }
    P.active = on;
    if (on) {
      const rvx = (x - P.px) / dt, rvy = (y - P.py) / dt; P.px = x; P.py = y;
      const cap = 5000, m = Math.hypot(rvx, rvy), f = m > cap ? cap / m : 1;
      P.vx += (rvx * f - P.vx) * 0.45; P.vy += (rvy * f - P.vy) * 0.45;
      P.x = x; P.y = y;
    } else { P.vx *= 0.8; P.vy *= 0.8; }
    const kL = 1 - Math.exp(-dt * 18);
    P.lx += (P.x - P.lx) * kL; P.ly += (P.y - P.ly) * kL;
    hover += ((on ? 1 : 0) - hover) * (1 - Math.exp(-dt * (on ? 9 : 5)));
    speed += (Math.min(1.5, Math.hypot(P.vx, P.vy) / 1500) - speed) * 0.2;

    // Inertie au défilement : le logo traîne un peu puis revient en ondulant
    const lag = Math.max(-0.22 * G.logoH, Math.min(0.22 * G.logoH, scrollV * 0.006 * cfg.int));
    const { GX, GY } = G;
    for (let i = 0; i < GX; i++) { const w = 0.72 + 0.28 * Math.sin(i / GX * Math.PI * 2.4 + 0.6); for (let j = 0; j < GY; j++) tY[j * GX + i] = lag * w; }

    applyPokes(R);
    const steps = Math.min(4, Math.max(1, Math.round(dt / DT)));
    for (let s = 0; s < steps; s++) step(on, R);
    energy = upload();

    const idle = !on && hover < 0.01 && energy < 0.02 && Math.abs(scrollV) < 2;
    idleFlip = !idleFlip;
    if (idle && idleFlip && !firstFrame) return;   // 30 i/s au repos

    const t = (now - t0) / 1000, dpr = G.dpr;
    gl.uniform2f(U.uRes, canvas.width, canvas.height);
    gl.uniform2f(U.uPtr, P.lx * dpr, P.ly * dpr);
    gl.uniform1f(U.uHover, hover);
    gl.uniform1f(U.uRadius, R * dpr);
    gl.uniform1f(U.uLens, 0.22 * cfg.int * (1 + 0.6 * Math.min(1, speed)));
    gl.uniform1f(U.uAb, cfg.ab);
    gl.uniform1f(U.uAbCap, Math.min(12, G.logoH * 0.08));
    gl.uniform1f(U.uM, G.M * dpr);
    gl.uniform1f(U.uDpr, dpr);
    gl.uniform1f(U.uSpeed, speed);
    gl.uniform1f(U.uTime, t);
    gl.uniform2f(U.uGhost, (G.logoX + G.logoW * (0.5 + 0.44 * Math.sin(t * 0.21))) * dpr, (G.logoY + G.logoH * (0.5 + 0.2 * Math.sin(t * 0.37))) * dpr);
    gl.uniform1f(U.uGhostR, G.logoH * 0.95 * dpr);
    gl.uniform1f(U.uGhostL, 0.035 * (1 - hover) * Math.min(1, cfg.int + 0.3));
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (firstFrame) { firstFrame = false; brand.classList.add('is-live'); }
  };
  const wake = () => { if (!running && visible && !document.hidden) { running = true; last = 0; lastScrollY = window.scrollY; raf = requestAnimationFrame(frame); } };

  const io = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; if (visible) wake(); }, { rootMargin: '120px 0px' });
  io.observe(wordmark);
  document.addEventListener('visibilitychange', wake, opt);

  let resizeT = 0;
  const relayout = () => { if (layout()) { firstFrame = true; wake(); } };
  const ro = new ResizeObserver(() => { clearTimeout(resizeT); resizeT = setTimeout(relayout, 120); });
  ro.observe(footer);
  canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); cancelAnimationFrame(raf); running = false; brand.classList.remove('is-live'); }, { signal: ac.signal });
  canvas.addEventListener('webglcontextrestored', () => { try { initGL(); relayout(); } catch (_) {} }, { signal: ac.signal });
  if (!layout()) { /* footer pas encore mesurable : le ResizeObserver relancera */ }
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(relayout);

  return () => {   // nettoyage (éditeur de thème)
    ac.abort(); io.disconnect(); ro.disconnect(); cancelAnimationFrame(raf); clearTimeout(resizeT);
    running = false; visible = false; brand.classList.remove('is-live'); canvas.remove();
    const lose = gl.getExtension('WEBGL_lose_context'); if (lose) lose.loseContext();
  };
}

/* ---------- Démarrage paresseux ---------- */
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
let stop = null, watcher = null;

function arm() {
  if (stop || watcher) return;
  const wordmark = document.querySelector('.av-footer-wordmark');
  if (!wordmark || reduce.matches) return;
  // Le WebGL ne s'initialise que lorsque le footer approche de l'écran.
  watcher = new IntersectionObserver((entries) => {
    if (!entries[0].isIntersecting) return;
    watcher.disconnect(); watcher = null;
    try { stop = start(wordmark); } catch (_) { stop = null; }
  }, { rootMargin: '600px 0px' });
  watcher.observe(wordmark);
}
function disarm() {
  if (watcher) { watcher.disconnect(); watcher = null; }
  if (stop) { try { stop(); } catch (_) {} stop = null; }
}

arm();
reduce.addEventListener?.('change', () => { disarm(); arm(); });
document.addEventListener('shopify:section:unload', (e) => { if (e.target.querySelector?.('.av-footer-wordmark')) disarm(); });
document.addEventListener('shopify:section:load', (e) => { if (e.target.querySelector?.('.av-footer-wordmark')) { disarm(); arm(); } });
